import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, isSubscribed } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EpisodeCard from "@/components/EpisodeCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Crown, Play, Search } from "lucide-react";
import { LOGO_URL } from "@/lib/branding";

export default function Browse() {
  const { user } = useAuth();
  const subscribed = isSubscribed(user);
  const [episodes, setEpisodes] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [continueList, setContinueList] = useState([]);
  const [shows, setShows] = useState([]);
  const [activeShow, setActiveShow] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/episodes").then(r => setEpisodes(r.data)).catch(() => {});
    api.get("/shows").then(r => setShows(r.data)).catch(() => {});
    if (user && user !== false) {
      api.get("/watchlist").then(r => setWatchlist(r.data)).catch(() => {});
      api.get("/continue-watching").then(r => setContinueList(r.data)).catch(() => {});
    }
  }, [user]);

  const filtered = useMemo(() => {
    let list = episodes;
    if (activeShow !== "all") list = list.filter(e => e.show === activeShow);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(e =>
        (e.title || "").toLowerCase().includes(needle) ||
        (e.description || "").toLowerCase().includes(needle) ||
        (e.show || "").toLowerCase().includes(needle)
      );
    }
    return list;
  }, [episodes, activeShow, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(ep => {
      const k = ep.show || "Other";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ep);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const featured = episodes[0];

  return (
    <div className="min-h-screen bg-brand-bg text-white">
      <Navbar />

      {/* Featured hero — no stock imagery */}
      {featured && (
        <section className="relative h-[62vh] md:h-[72vh] overflow-hidden" data-testid="browse-featured">
          <div className="absolute inset-0" style={{
            background:
              "radial-gradient(70% 80% at 80% 25%, rgba(255,0,85,0.32) 0%, transparent 60%)," +
              "radial-gradient(40% 50% at 10% 80%, rgba(0,240,255,0.10) 0%, transparent 70%)," +
              "linear-gradient(180deg, #08020a 0%, #050505 60%, #050505 100%)",
          }} />
          <div className="absolute inset-0 hero-grad" />
          <div className="absolute inset-y-0 right-0 w-[55%] opacity-[0.06] pointer-events-none"
               style={{ backgroundImage: "repeating-linear-gradient(115deg, #fff 0 1px, transparent 1px 18px)" }} />

          <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-12 md:pb-16">
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_URL} alt="ball0ut.ent" className="h-10 w-10 object-contain" />
              <div className="text-xs uppercase tracking-[0.3em] text-brand-primary">ball0ut.ent · Premiere</div>
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-tight max-w-3xl">Boyz Gone Wild</h1>
            <p className="text-brand-muted max-w-xl mt-4">
              The wildest, most unfiltered boyz in one location. No rules. No limits. All on footage.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to={`/watch/${featured.id}`} data-testid="browse-play-featured">
                <Button className="bg-brand-primary hover:bg-brand-primaryHover rounded-none px-7 h-12 uppercase tracking-[0.15em] font-semibold">
                  <Play className="h-4 w-4 mr-2 fill-white" /> Play S{featured.season} · E{featured.episode_number}
                </Button>
              </Link>
              {!subscribed && user?.role !== "admin" && (
                <Link to="/subscribe" data-testid="browse-go-premium">
                  <Button variant="outline" className="border-white/30 bg-white/5 hover:bg-white/15 text-white rounded-none px-7 h-12 uppercase tracking-[0.15em]">
                    <Crown className="h-4 w-4 mr-2" /> Go Premium
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Search + Show filter */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 mt-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div className="relative md:w-[420px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <Input
              data-testid="browse-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search episodes, shows…"
              className="pl-10 bg-black border-white/10 text-white rounded-none h-12 focus-visible:ring-brand-primary focus-visible:ring-offset-0"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="browse-show-tabs">
            <button
              data-testid="show-tab-all"
              onClick={() => setActiveShow("all")}
              className={`px-4 h-10 border text-xs uppercase tracking-[0.18em] ${activeShow === "all" ? "bg-brand-primary border-brand-primary text-white" : "border-white/15 text-brand-muted hover:text-white hover:border-white/30"}`}
            >
              All shows
            </button>
            {shows.map(s => (
              <button
                key={s.name}
                data-testid={`show-tab-${s.name.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setActiveShow(s.name)}
                className={`px-4 h-10 border text-xs uppercase tracking-[0.18em] ${activeShow === s.name ? "bg-brand-primary border-brand-primary text-white" : "border-white/15 text-brand-muted hover:text-white hover:border-white/30"}`}
              >
                {s.name} <span className="opacity-60 ml-1">{s.episode_count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Continue Watching */}
      {continueList.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-12 mt-12" data-testid="browse-continue-watching">
          <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">Continue Watching</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {continueList.map((ep) => (
              <EpisodeCard
                key={ep.id} ep={ep}
                locked={!subscribed && ep.is_premium}
                progressPercent={ep.progress_percent || 0}
              />
            ))}
          </div>
        </section>
      )}

      {/* Watchlist */}
      {watchlist.length > 0 && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-12 mt-14" data-testid="browse-watchlist">
          <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">My Watchlist</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {watchlist.map((ep) => (
              <EpisodeCard key={ep.id} ep={ep} locked={!subscribed && ep.is_premium} />
            ))}
          </div>
        </section>
      )}

      {/* All episodes grouped by show */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 mt-14 pb-20" data-testid="browse-all-episodes">
        {filtered.length === 0 ? (
          <div className="border border-white/10 bg-brand-surface p-10 text-center text-brand-muted">
            No episodes match your search.
          </div>
        ) : (
          grouped.map(([showName, eps]) => (
            <div key={showName} className="mb-12">
              <div className="flex items-end justify-between mb-6">
                <h2 className="font-display text-2xl md:text-3xl uppercase">{showName}</h2>
                <div className="text-xs uppercase tracking-[0.18em] text-brand-muted">{eps.length} episodes</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {eps.map((ep, i) => (
                  <EpisodeCard key={ep.id} ep={ep} locked={!subscribed && ep.is_premium} index={i} />
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <Footer />
    </div>
  );
}
